import { getKindeInstance } from '../auth/kindeClient';
import { getOnlineUsers } from '../services/PresenceService.js';
import { ownerBadgeHTML } from './ownerBadge.js';
import { leagueForWins, LEAGUE_TIERS } from './leagues.js';

// Resolve "wins" for a leaderboard row. Prefer the real wins field
// (sent on the user's own record from /api/user/...), and fall back to
// a score-based proxy when the leaderboard payload omits it (which
// /api/leaderboard currently does — it only sends rank/username/score).
// 500 score ≈ 1 league-rank-point matches the spread of the live
// production leaderboard pretty closely: top player rolls into Ruby,
// mid-tier into Silver/Iron, newcomers stay Bronze.
const SCORE_TO_WIN_DIVISOR = 500;
export function effectiveWinsFor(user) {
    if (!user) return 0;
    const w = Number(user.wins);
    if (Number.isFinite(w) && w >= 0) return w;
    const s = Number(user.score) || 0;
    return Math.max(0, Math.floor(s / SCORE_TO_WIN_DIVISOR));
}

// Build the HTML chip for a leaderboard row's league badge. Always
// renders something — uses the score-derived proxy when no real wins
// value is present, so the leaderboard never goes badge-less.
function leagueBadgeHTML(user) {
    if (!user) return '';
    const wins = effectiveWinsFor(user);
    const tier = leagueForWins(wins);
    if (!tier) return '';
    return `<span class="league-badge league-${tier.key}" title="${tier.name} league · ${wins} wins">${tier.name}</span>`;
}

// Defensive sort so list order is stable + explicit even if the server
// returns rows in arbitrary order. Primary: ascending rank when present;
// secondary: descending score; tertiary: descending wins.
function sortLeaderboardRows(rows) {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => {
        const ar = Number(a.rank), br = Number(b.rank);
        if (Number.isFinite(ar) && Number.isFinite(br) && ar !== br) return ar - br;
        const as = Number(a.score) || 0, bs = Number(b.score) || 0;
        if (as !== bs) return bs - as;
        const aw = Number(a.wins) || 0, bw = Number(b.wins) || 0;
        return bw - aw;
    });
}

const SERVER_URL = '';

let leaderboardRefreshInterval = null;

const fetchLeaderboard = async () => {
    try {
        const kinde = getKindeInstance();
        const token = await kinde.getToken();
        // limit=999 — most pagination backends respect this; if the
        // server ignores unknown params nothing changes. Goal: make
        // sure we receive every ranked user, not just the first page.
        const response = await fetch(`${SERVER_URL}/api/leaderboard?limit=999`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch leaderboard');
        }

        const leaderboard = await response.json();
        return leaderboard;
    } catch (error) {
        console.error('Error fetching leaderboard:', error);
        return [];
    }
};

const displayLeaderboard = async () => {
    const [leaderboard, onlineUsers] = await Promise.all([
        fetchLeaderboard(),
        getOnlineUsers()
    ]);
    
    // Create a Set of online usernames for quick lookup
    const onlineUsernames = new Set(onlineUsers.map(user => user.username));
    
    const leaderboardList = document.getElementById('leaderboardList');
    // The inline mini-leaderboard was removed when the lobby moved to the
    // 6-button grid; full board lives in #fullLeaderboardModal now. Keep
    // the auto-refresh running but skip rendering the missing list.
    if (!leaderboardList) {
        startLeaderboardAutoRefresh();
        return;
    }
    leaderboardList.innerHTML = '';

    const sortedTop = sortLeaderboardRows(leaderboard).slice(0, 3);
    sortedTop.forEach((user) => {
        const listItem = document.createElement('li');
        const isOnline = onlineUsernames.has(user.username);
        const onlineIndicator = isOnline
            ? '<span class="online-indicator" title="Online"></span>'
            : '';
        const wins = effectiveWinsFor(user);
        listItem.innerHTML = `<span class="rank">${user.rank}</span> <span class="username">${onlineIndicator}${user.username}${ownerBadgeHTML(user.username)}${leagueBadgeHTML(user)}</span> <span class="wins" title="Wins">${wins}W</span> <span class="score">${user.score}</span>`;
        leaderboardList.appendChild(listItem);
    });

    const viewAllButton = document.createElement('button');
    viewAllButton.textContent = 'View All';
    viewAllButton.onclick = showFullLeaderboardModal;
    leaderboardList.appendChild(viewAllButton);
    
    // Start auto-refresh if not already started
    startLeaderboardAutoRefresh();
};

const showFullLeaderboardModal = async () => {
    const [leaderboard, onlineUsers] = await Promise.all([
        fetchLeaderboard(),
        getOnlineUsers()
    ]);
    
    // Create a Set of online usernames for quick lookup
    const onlineUsernames = new Set(onlineUsers.map(user => user.username));
    
    const fullLeaderboardList = document.getElementById('fullLeaderboardList');
    fullLeaderboardList.innerHTML = '';

    // Duolingo-style grouping: bucket every player into their league tier,
    // then render highest-tier first with a clear divider per league. Inside
    // each league players are still ranked by score (server-given rank or
    // effective wins as fallback).
    const sortedAll = sortLeaderboardRows(leaderboard);
    const buckets = new Map(); // league.key → array of user rows
    for (const user of sortedAll) {
        const wins = effectiveWinsFor(user);
        const tier = leagueForWins(wins);
        const k = tier.key;
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push(user);
    }
    // Walk LEAGUE_TIERS in REVERSE (Ruby first → Bronze last) so the top
    // league sits at the top of the board. Empty tiers are skipped.
    const orderedKeys = [...LEAGUE_TIERS].reverse().map(t => t.key);
    for (const key of orderedKeys) {
        const players = buckets.get(key);
        if (!players || players.length === 0) continue;
        const tier = LEAGUE_TIERS.find(t => t.key === key);

        const sectionHeader = document.createElement('li');
        sectionHeader.className = `leaderboard-league-header league-${tier.key}`;
        sectionHeader.innerHTML = `<span class="league-badge league-${tier.key}">${tier.name}</span><span class="league-count">${players.length} player${players.length === 1 ? '' : 's'}</span>`;
        fullLeaderboardList.appendChild(sectionHeader);

        players.forEach((user, idxInLeague) => {
            const listItem = document.createElement('li');
            listItem.classList.add('leaderboard-league-row');
            const isOnline = onlineUsernames.has(user.username);
            const onlineIndicator = isOnline
                ? '<span class="online-indicator" title="Online"></span>'
                : '';
            const wins = effectiveWinsFor(user);
            // Per-league rank (1, 2, 3 …) instead of the global rank so each
            // league reads as its own podium — same duolingo trick.
            const leagueRank = idxInLeague + 1;
            listItem.innerHTML = `<span class="rank">${leagueRank}</span> <span class="username">${onlineIndicator}${user.username}${ownerBadgeHTML(user.username)}</span> <span class="wins" title="Wins">${wins}W</span> <span class="score">${user.score}</span>`;
            fullLeaderboardList.appendChild(listItem);
        });
    }

    const fullLeaderboardModal = document.getElementById('fullLeaderboardModal');
    const overlay = document.getElementById('overlay');
    fullLeaderboardModal.style.display = 'block';
    overlay.style.display = 'block';
};

/**
 * Starts automatic refreshing of the leaderboard to keep online status updated
 */
const startLeaderboardAutoRefresh = () => {
    if (leaderboardRefreshInterval) {
        return; // Already running
    }
    
    // Refresh leaderboard every 45 seconds to show updated online status
    leaderboardRefreshInterval = setInterval(() => {
        displayLeaderboard();
    }, 45000);
};

/**
 * Stops automatic refreshing of the leaderboard
 */
const stopLeaderboardAutoRefresh = () => {
    if (leaderboardRefreshInterval) {
        clearInterval(leaderboardRefreshInterval);
        leaderboardRefreshInterval = null;
    }
};

export { fetchLeaderboard, displayLeaderboard, showFullLeaderboardModal, startLeaderboardAutoRefresh, stopLeaderboardAutoRefresh };
